/**
 * Договор своей номенклатуры (`Resource.Own.Contracts`, RP-OWN1) на границе клиента: ответ проверяется здесь, экран получает
 * только проверенные поля (D-056). Цены приходят числом `decimal` — в модель они идут копейками целым числом, без умножения
 * с плавающей точкой (`kopecksOf`).
 */

export interface OwnProductRow {
  id: string
  name: string
  sku: string | null
  unitName: string | null
  groupId: string | null
  vatRate: string | null
  barcodes: readonly string[]
  archived: boolean
  version: number
}

export interface OwnProductsPage {
  items: readonly OwnProductRow[]
  /** Курсор следующей страницы; `null` — страница последняя. */
  nextCursor: string | null
}

export interface OwnCurrentPrice {
  priceTypeId: string
  priceTypeName: string
  kopecks: number
  currency: string
  validFrom: string
}

export interface OwnProduct {
  id: string
  name: string
  fullName: string | null
  sku: string | null
  unitCode: string | null
  unitName: string | null
  /** `null` — весовой признак не определён: товар не продаётся, пока его не выберут. */
  isWeighted: boolean | null
  groupId: string | null
  vatRate: string | null
  paymentSubject: number | null
  paymentMethod: number | null
  isMarked: boolean
  barcodes: readonly string[]
  archived: boolean
  version: number
  createdAt: string
  updatedAt: string
  pricesOn: string
  prices: readonly OwnCurrentPrice[]
}

export interface OwnGroup {
  id: string
  name: string
  parentId: string | null
  archived: boolean
  version: number
}

/**
 * Хозяин цен вида (RP-SY7): `own` — своя база, цены вида раздаются в источники (1С, AQSI); `source` — цены ведутся в другом источнике
 * (например закупочная в 1С) и не раздаются.
 */
export type OwnPriceOwner = 'own' | 'source'

export interface OwnPriceKind {
  id: string
  name: string
  currency: string
  archived: boolean
  version: number
  owner: OwnPriceOwner
}

export interface OwnPriceRecord {
  productId: string
  priceTypeId: string
  validFrom: string
  kopecks: number
  setBy: string
  setAt: string
}

/** Тело записи товара (`OwnProductInput`): отсутствующее значение — `null`, не пустая строка. */
export interface OwnProductInput {
  name: string
  fullName: string | null
  sku: string | null
  unitCode: string | null
  unitName: string | null
  /** `null` — весовой признак не определён: товар не продаётся, пока его не выберут. */
  isWeighted: boolean | null
  groupId: string | null
  vatRate: string | null
  paymentSubject: number | null
  paymentMethod: number | null
  isMarked: boolean
  barcodes: readonly string[]
}

function fail(what: string): never {
  throw new Error(`Ответ своей номенклатуры не по договору: ${what}`)
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const record = (value: unknown, what: string): Record<string, unknown> => (isRecord(value) ? value : fail(what))

const required = (value: unknown, what: string): string => (typeof value === 'string' && value !== '' ? value : fail(what))

const optional = (value: unknown, what: string): string | null =>
  value === null || value === undefined ? null : typeof value === 'string' ? value : fail(what)

const flag = (value: unknown, what: string): boolean => (typeof value === 'boolean' ? value : fail(what))

const optionalFlag = (value: unknown, what: string): boolean | null =>
  value === null || value === undefined ? null : typeof value === 'boolean' ? value : fail(what)

const whole = (value: unknown, what: string): number => (typeof value === 'number' && Number.isSafeInteger(value) ? value : fail(what))

const code = (value: unknown, what: string): number | null => (value === null || value === undefined ? null : whole(value, what))

const list = (value: unknown, what: string): readonly unknown[] => (Array.isArray(value) ? value : fail(what))

const strings = (value: unknown, what: string): readonly string[] =>
  list(value, what).map((item, index) => required(item, `${what}[${index}]`))

/**
 * Цена `decimal` → копейки целым числом. Число переводится в текст и режется по точке — не `value * 100`: 0,29 × 100 в
 * плавающей точке даёт 28,999…, и копейка терялась бы. Больше двух знаков служба не хранит; такое — нарушение договора.
 */
export function kopecksOf(value: unknown, what = 'цена'): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return fail(what)
  const text = String(value)
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return fail(what)
  const [rubles = '0', fraction = ''] = text.split('.')
  const kopecks = Number.parseInt(rubles, 10) * 100 + Number.parseInt(fraction.padEnd(2, '0'), 10)
  return Number.isSafeInteger(kopecks) ? kopecks : fail(what)
}

/** Копейки → число `decimal` для тела запроса: «12050» → 120.5, без деления с плавающей точкой до текста. */
export function decimalOf(kopecks: number): number {
  const sign = kopecks < 0 ? '-' : ''
  const absolute = Math.abs(Math.trunc(kopecks))
  return Number(`${sign}${Math.trunc(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`)
}

export function decodeProductRow(raw: unknown, what = 'товар'): OwnProductRow {
  const row = record(raw, what)
  return {
    id: required(row.id, `${what}.id`),
    name: required(row.name, `${what}.name`),
    sku: optional(row.sku, `${what}.sku`),
    unitName: optional(row.unitName, `${what}.unitName`),
    groupId: optional(row.groupId, `${what}.groupId`),
    vatRate: optional(row.vatRate, `${what}.vatRate`),
    barcodes: strings(row.barcodes ?? [], `${what}.barcodes`),
    archived: flag(row.archived, `${what}.archived`),
    version: whole(row.version, `${what}.version`),
  }
}

export function decodeProductsPage(raw: unknown): OwnProductsPage {
  const page = record(raw, 'страница товаров')
  return {
    items: list(page.items, 'items').map((item, index) => decodeProductRow(item, `items[${index}]`)),
    nextCursor: optional(page.nextCursor, 'nextCursor'),
  }
}

export function decodeProduct(raw: unknown): OwnProduct {
  const card = record(raw, 'карточка товара')
  const row = decodeProductRow(card, 'карточка товара')
  return {
    ...row,
    fullName: optional(card.fullName, 'fullName'),
    unitCode: optional(card.unitCode, 'unitCode'),
    isWeighted: optionalFlag(card.isWeighted, 'isWeighted'),
    paymentSubject: code(card.paymentSubject, 'paymentSubject'),
    paymentMethod: code(card.paymentMethod, 'paymentMethod'),
    isMarked: flag(card.isMarked, 'isMarked'),
    createdAt: required(card.createdAt, 'createdAt'),
    updatedAt: required(card.updatedAt, 'updatedAt'),
    pricesOn: required(card.pricesOn, 'pricesOn'),
    prices: list(card.prices ?? [], 'prices').map((item, index) => {
      const price = record(item, `prices[${index}]`)
      return {
        priceTypeId: required(price.priceTypeId, `prices[${index}].priceTypeId`),
        priceTypeName: required(price.priceTypeName, `prices[${index}].priceTypeName`),
        kopecks: kopecksOf(price.price, `prices[${index}].price`),
        currency: required(price.currency, `prices[${index}].currency`),
        validFrom: required(price.validFrom, `prices[${index}].validFrom`),
      }
    }),
  }
}

export function decodeGroup(raw: unknown, what = 'группа'): OwnGroup {
  const group = record(raw, what)
  return {
    id: required(group.id, `${what}.id`),
    name: required(group.name, `${what}.name`),
    parentId: optional(group.parentId, `${what}.parentId`),
    archived: flag(group.archived, `${what}.archived`),
    version: whole(group.version, `${what}.version`),
  }
}

export function decodeGroups(raw: unknown): readonly OwnGroup[] {
  return list(raw, 'группы').map((item, index) => decodeGroup(item, `группы[${index}]`))
}

export function decodePriceKind(raw: unknown, what = 'вид цены'): OwnPriceKind {
  const kind = record(raw, what)
  return {
    id: required(kind.id, `${what}.id`),
    name: required(kind.name, `${what}.name`),
    currency: required(kind.currency, `${what}.currency`),
    archived: flag(kind.archived, `${what}.archived`),
    version: whole(kind.version, `${what}.version`),
    owner: kind.owner === 'own' || kind.owner === 'source' ? kind.owner : fail(`${what}.owner`),
  }
}

export function decodePriceKinds(raw: unknown): readonly OwnPriceKind[] {
  return list(raw, 'виды цен').map((item, index) => decodePriceKind(item, `виды цен[${index}]`))
}

export function decodePriceRecord(raw: unknown, what = 'цена'): OwnPriceRecord {
  const price = record(raw, what)
  return {
    productId: required(price.productId, `${what}.productId`),
    priceTypeId: required(price.priceTypeId, `${what}.priceTypeId`),
    validFrom: required(price.validFrom, `${what}.validFrom`),
    kopecks: kopecksOf(price.price, `${what}.price`),
    setBy: required(price.setBy, `${what}.setBy`),
    setAt: required(price.setAt, `${what}.setAt`),
  }
}

export function decodePriceRecords(raw: unknown): readonly OwnPriceRecord[] {
  return list(raw, 'цены').map((item, index) => decodePriceRecord(item, `цены[${index}]`))
}
